import { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router';
import { recordRumPageView } from 'src/common/rum/rum';
import { RUM_PAGE_NAME, PageName } from 'src/common/constants';
import {
  HOME_PATH,
  NAMESPACE_ROUTE,
  ROOT_PATH,
  SEARCH_PATH,
  STRING_GROUP_ROUTE,
  STRING_ROUTE,
  TRACK_MY_TRANSLATIONS_PATH,
} from 'src/common/routes';

/**
 * Resolve the page label for a pathname (and optional query string). Order matters: the most
 * specific route patterns (`/ns/:id/sg/:id/str/:id`) are tested before the less specific
 * (`/ns/:id`) so a String Detail path isn't mislabelled as Namespace.
 *
 * `matchPath` requires a full-path match (not a prefix), so `/ns/a/sg/b` does not match the
 * String Detail pattern — each depth maps to exactly one label.
 *
 * The string-detail route carries an optional `?variant=<cxId>` query param (base string vs. a
 * CX-specific variant differ only by this param, not the pathname). When it is present the page is
 * labelled {@link RUM_PAGE_NAME.Variant} rather than StringDetail, so variant views are counted
 * separately and switching between variants re-counts.
 *
 * @param pathname the location pathname
 * @param search   the location query string (e.g. `?variant=test1`); optional, defaults to none
 */
export function resolvePageName(pathname: string, search = ''): PageName {
  // Landing: both the root probe and the marketing shell count as one page.
  if (pathname === ROOT_PATH || pathname === HOME_PATH) {
    return RUM_PAGE_NAME.Landing;
  }
  if (matchPath(SEARCH_PATH, pathname)) {
    return RUM_PAGE_NAME.Search;
  }
  if (matchPath(TRACK_MY_TRANSLATIONS_PATH, pathname)) {
    return RUM_PAGE_NAME.Track;
  }
  // Most specific dynamic route first.
  if (matchPath(STRING_ROUTE, pathname)) {
    // A `?variant` on the string-detail route is the variant page; otherwise the base string.
    return new URLSearchParams(search).has('variant')
      ? RUM_PAGE_NAME.Variant
      : RUM_PAGE_NAME.StringDetail;
  }
  if (matchPath(STRING_GROUP_ROUTE, pathname)) {
    return RUM_PAGE_NAME.StringGroup;
  }
  if (matchPath(NAMESPACE_ROUTE, pathname)) {
    return RUM_PAGE_NAME.Namespace;
  }
  return RUM_PAGE_NAME.Other;
}

/**
 * Emit a RUM page-view record on each client-side route change.
 *
 * RUM's automatic page-view recording is disabled (see rum.ts `disableAutoPageView`), so this hook
 * is the sole source of in-app RUM page views. It fires on every react-router location change and
 * records a stable page label (see {@link resolvePageName}), so views aggregate per page rather
 * than per id. No-op until RUM is initialized.
 *
 * The string-detail route additionally re-fires when the `?variant=<cxId>` query param changes:
 * base string ↔ variant and variant ↔ variant are same-pathname transitions, so the effect also
 * depends on the `variant` value (not the whole query string — unrelated params like `version`
 * must not trigger a spurious page view). Such views are labelled {@link RUM_PAGE_NAME.Variant}.
 *
 * NOTE: the external Help link is not a route change, so it is emitted separately from its own click
 * handler (see AppHeader).
 */
export function usePageViewMetrics(): void {
  const { pathname, search } = useLocation();
  // Depend on the specific `variant` param, not the whole search string, so switching variants
  // re-counts while a change to any other query param does not.
  const variant = new URLSearchParams(search).get('variant');

  useEffect(() => {
    recordRumPageView(resolvePageName(pathname, search));
    // `search` is intentionally excluded from the deps: `variant` (derived from it) is the only
    // query param that should re-fire a page view. Including `search` would re-count on any param
    // change (e.g. `version`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, variant]);
}
